import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import { Autocomplete, Box, Button, Chip, Divider, Grid, IconButton, TextField } from '@mui/material';
import { assignleadtoOGA, getAllOGAs, getLeadsUnderOGA, getOGAunderSOGO } from '../../../../redux/features/ogdSlice';
import { useFormik } from 'formik';
import { Icon } from '@iconify/react/dist/iconify.js';
import * as Yup from 'yup';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import { useSnackbar } from 'notistack';
import LeadDetailsSearchTable from './components/LeadDetailsSearchTable';

const AssignLeads = () => {
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.ogd.loading);
  const ogaunderSogo = useSelector((state) => state.ogd.ogaunderSogo);
  const AllogaList = useSelector((state) => state.ogd.AllogaList);
  const { enqueueSnackbar } = useSnackbar();

  const [leads, setLeads] = useState([]);

  // ✅ Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ogaIds: [],
      leads: [],
      newogaID: null,
      search: '',
    },
    validationSchema: Yup.object().shape({
      ogaIds: Yup.array().min(1, 'Select at least one OGA'),
      leads: Yup.array().min(1, 'Select at least one lead'),
      newogaID: Yup.object().nullable().required('Select new OGA'),
    }),
    onSubmit: (values) => {
      const payload = {
        leadIds: values.leads.map((item) => item.lead_id),
        ogaId: values.newogaID?.userID || null,
      };

      dispatch(assignleadtoOGA(payload)).then((res) => {
        if (res.payload.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          formik.resetForm();
        } else {
          enqueueSnackbar(res.payload.message, { variant: 'error' });
        }
      });
    },
  });

  const isNotAssigened = useMemo(() => formik.values.ogaIds[0]?.userID === -1, [formik.values.ogaIds]);

  useEffect(() => {
    dispatch(getOGAunderSOGO());
    dispatch(getAllOGAs());
  }, [dispatch]);

  useEffect(() => {
    let getLeadsUnderOGASignal = null;
    if (formik.values.ogaIds.length > 0) {
      const ogaIds = formik.values.ogaIds.map((b) => b.userID);
      getLeadsUnderOGASignal = dispatch(getLeadsUnderOGA({ ogaIds, search: formik.values.search }));
      getLeadsUnderOGASignal.then((res) => {
        if (res.payload?.status === 'success') {
          setLeads(res.payload.data || []);
        } else {
          setLeads([]);
        }
      });
    } else {
      setLeads([]);
    }

    return () => {
      if (typeof getLeadsUnderOGASignal?.abort === 'function') getLeadsUnderOGASignal.abort();
    };
  }, [formik.values.ogaIds, formik.values.search, dispatch]);

  return (
    <GlobalWrapper title={'OGD Migration - Leads Reassignment'}>
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3} sm={3} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <Autocomplete
              sx={{
                flex: 1,
                minWidth: 0,
              }}
              multiple
              disableCloseOnSelect
              options={[
                ...(formik.values.ogaIds?.length <= 0 || isNotAssigened
                  ? [
                      {
                        name: 'unknown',
                        userID: -1,
                        finalName: 'NOT ASSIGNED',
                      },
                    ]
                  : []),
                ...ogaunderSogo,
              ]}
              getOptionLabel={(option) => option.finalName || ''}
              disabled={isNotAssigened}
              value={formik.values.ogaIds}
              onChange={(e, value) => formik.setFieldValue('ogaIds', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose OGA(s)"
                  placeholder="Choose OGA(s)"
                  error={formik.touched.ogaIds && Boolean(formik.errors.ogaIds)}
                  helperText={formik.touched.ogaIds && formik.errors.ogaIds}
                />
              )}
            />
            {isNotAssigened && (
              <IconButton
                onClick={() => {
                  formik.setFieldValue('ogaIds', []);
                  formik.setFieldValue('search', '');
                }}
              >
                <DisabledByDefaultIcon />
              </IconButton>
            )}
          </Grid>
          <Grid item xs={12} md={3} sm={3}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={!isNotAssigened && leads.length === 0}
              options={[
                ...(isNotAssigened
                  ? []
                  : [
                      {
                        lead_id: 'ALL',
                        lead_name: 'Select All Students',
                        lead_mob_no: '',
                      },
                    ]),
                ...leads,
              ]}
              getOptionLabel={(option) =>
                option.lead_id === 'ALL' ? option.lead_name : `${option.lead_name} (${option.lead_mob_no})`
              }
              value={formik.values.leads}
              onChange={(e, value) => {
                if (value.some((v) => v.lead_id === 'ALL')) {
                  if (formik.values.leads.length === leads.length) {
                    formik.setFieldValue('leads', []);
                  } else {
                    formik.setFieldValue('leads', leads);
                  }
                } else {
                  formik.setFieldValue('leads', value);
                }
              }}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...(isNotAssigened ? { onBlur: (e) => formik.setFieldValue('search', '') } : {})}
                  label={
                    loading
                      ? 'Loading Data'
                      : leads.length === 0 && formik.values.ogaIds.length > 0
                        ? 'No Active Students'
                        : 'Select Students'
                  }
                  {...(isNotAssigened ? { onChange: (e) => formik.setFieldValue('search', e.target.value) } : {})}
                  placeholder="Select Students"
                  error={formik.touched.leads && Boolean(formik.errors.leads)}
                  helperText={formik.touched.leads && formik.errors.leads}
                />
              )}
              renderTags={
                leads.length === formik.values.leads.length
                  ? () => {
                      return (
                        <Chip
                          label="All Students Selected"
                          color="primary"
                          variant="filled"
                          icon={<Icon icon="mdi:check-all" width={20} height={20} />}
                        />
                      );
                    }
                  : null
              }
            />
          </Grid>
          <Grid item xs={12} md={3} sm={3}>
            <Autocomplete
              options={AllogaList}
              getOptionLabel={(option) => option.finalName || ''}
              value={formik.values.newogaID}
              onChange={(e, value) => formik.setFieldValue('newogaID', value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose New OGA"
                  placeholder="Choose New OGA"
                  error={formik.touched.newogaID && Boolean(formik.errors.newogaID)}
                  helperText={formik.touched.newogaID && formik.errors.newogaID}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3} sm={3}>
            <Button
              variant="contained"
              startIcon={<Icon icon="lsicon:submit-filled" width={20} height={20} />}
              type="submit"
              color="secondary"
              disabled={loading && formik.isSubmitting}
              fullWidth
              size="large"
            >
              {loading && formik.isSubmitting ? 'Reassigning...' : 'Reassign Leads'}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Divider flexItem sx={{ my: 3 }} />
      <LeadDetailsSearchTable />
    </GlobalWrapper>
  );
};

export default AssignLeads;
